import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Extension as ModuleIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import authService from '../shared/services/authService';

const schema = yup.object().shape({
  name: yup.string().required('Group name is required'),
  description: yup.string().required('Description is required'),
});

interface GroupFormData {
  name: string;
  description: string;
}

interface GroupFormProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  group?: any | null;
}

interface Module {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
}

interface User {
  id: string;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
}

const GroupForm: React.FC<GroupFormProps> = ({ open, onClose, group }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [modules, setModules] = useState<Module[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GroupFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  useEffect(() => {
    if (group) {
      reset({
        name: group.name,
        description: group.description,
      });
      setSelectedModules(group.module_permissions?.map((m: any) => m.module_id) || []);
      setSelectedUsers(group.members?.map((u: any) => u.user_id) || []);
    } else {
      reset({
        name: '',
        description: '',
      });
      setSelectedModules([]);
      setSelectedUsers([]);
    }
  }, [group, reset]);

  const loadData = async () => {
    try {
      const [modulesData, usersData] = await Promise.all([
        authService.getModules(),
        authService.getAllUsers(),
      ]);
      setModules(modulesData.filter(m => m.is_active));
      setUsers(usersData.filter(u => u.role !== 'admin'));
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const onSubmit = async (data: GroupFormData) => {
    try {
      setIsLoading(true);
      
      let groupId: string;
      
      if (group) {
        const result = await authService.updateGroup(group.id, data);
        // Check if it's a placeholder response
        if (result.message && result.message.includes('to be implemented')) {
          toast.warn('Group management is not yet fully implemented');
          onClose(true);
          return;
        }
        groupId = group.id;
        toast.success('Group updated successfully');
      } else {
        const result = await authService.createGroup(data);
        // Check if it's a placeholder response
        if (result.message && result.message.includes('to be implemented')) {
          toast.warn('Group creation is not yet fully implemented. This is a preview of the admin interface.');
          onClose(true);
          return;
        }
        groupId = result.id;
        toast.success('Group created successfully');
      }

      // Only try to update module permissions if we have a real group ID and modules selected
      if (groupId && selectedModules.length > 0) {
        try {
          await authService.setGroupModulePermissions(groupId, selectedModules);
        } catch (error: any) {
          // Don't fail the entire operation if module permissions fail
          console.warn('Module permissions not implemented yet:', error);
          toast.info('Group created but module permissions are not yet implemented');
        }
      }

      // Update user assignments (simplified - would need proper API)
      // In a real implementation, you'd have separate endpoints for adding/removing users
      
      onClose(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to save group';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModuleChange = (moduleId: string, checked: boolean) => {
    if (checked) {
      setSelectedModules([...selectedModules, moduleId]);
    } else {
      setSelectedModules(selectedModules.filter(id => id !== moduleId));
    }
  };

  const handleUserChange = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {group ? 'Edit Group' : 'Create New Group'}
        <IconButton onClick={() => onClose()} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Group Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      fullWidth
                    />
                  )}
                />
              </Box>
            </Box>

            <Divider />

            {/* Module Permissions */}
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ModuleIcon color="primary" />
                <Typography variant="h6">
                  Module Permissions
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" mb={2}>
                Select which modules members of this group can access:
              </Typography>

              <FormGroup>
                {modules.map((module) => (
                  <FormControlLabel
                    key={module.id}
                    control={
                      <Checkbox
                        checked={selectedModules.includes(module.id)}
                        onChange={(e) => handleModuleChange(module.id, e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle2">{module.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {module.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>

              {selectedModules.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    Selected modules:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {selectedModules.map((moduleId) => {
                      const module = modules.find(m => m.id === moduleId);
                      return (
                        <Chip
                          key={moduleId}
                          label={module?.name}
                          size="small"
                          color="primary"
                          onDelete={() => handleModuleChange(moduleId, false)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>

            <Divider />

            {/* User Assignment */}
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PersonIcon color="primary" />
                <Typography variant="h6">
                  Group Members
                </Typography>
              </Box>
              
              <Typography variant="body2" color="textSecondary" mb={2}>
                Select users to add to this group:
              </Typography>

              <List dense sx={{ maxHeight: '200px', overflow: 'auto' }}>
                {users.map((user) => (
                  <ListItem key={user.id} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onChange={(e) => handleUserChange(user.id, e.target.checked)}
                        />
                      }
                      label=""
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={`${user.firstname} ${user.lastname}`}
                      secondary={`@${user.username} - ${user.email}`}
                    />
                  </ListItem>
                ))}
              </List>

              {selectedUsers.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    Selected users:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {selectedUsers.map((userId) => {
                      const user = users.find(u => u.id === userId);
                      return (
                        <Chip
                          key={userId}
                          label={`${user?.firstname} ${user?.lastname}`}
                          size="small"
                          color="secondary"
                          onDelete={() => handleUserChange(userId, false)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => onClose()} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : (group ? 'Update Group' : 'Create Group')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GroupForm;